import React, { useMemo, useState } from 'react'
import Breadcrumbs from '../components/Breadcrumbs'
import { createPortal } from 'react-dom'
import Card from '../components/Card'
import Avatar from '../components/Avatar'
import Skeleton from 'react-loading-skeleton'
import { useAxios } from '../hooks/useAxios'
import { getUserEducationList } from '../api/getUserEducationList'
import { toast } from 'react-toastify'
import EducationInfoItem from '../components/EducationInfoItem'
import { type IEducationItem } from '../server/user/info/education/list/get'
import { getUserLanguagesList } from '../api/getUserLanguagesList'
import Chip from '../components/Chip'
import { type ILanguageItem } from '../server/user/info/languages/list/get'
import { getUserOtherInfo } from '../api/getUserOtherInfo'
import ExpandedText from '../components/ExpandedText'
import ExpandedContent from '../components/ExpandedContent'
import CommunityItem from '../components/CommunityItem'
import { observer } from 'mobx-react-lite'
import { ConfigStore } from '../store/configStore'
import { IUserSocial } from '../server/user/get'
import Balls from '../components/Balls'
import { useParams } from 'react-router-dom'
import { getUserById } from '../api/getUserById'
import getArticles from '../api/getArticles'
import { ArticleData } from '../server/utils/getArticlesData'
import NotFound from '../components/NotFound'

const BREADCRUMBS = [
  {
    label: 'Главная',
    path: '/'
  },
  {
    label: 'Участники',
    path: ''
  }
]

const ParticipantPage = observer(() => {
  const { id } = useParams()
  const [portal, setPortal] = useState<HTMLElement | null>(null)

  const { response, fire } = useAxios()
  const { response: response2, fire: fire2 } = useAxios()
  const { response: response3, fire: fire3 } = useAxios()
  // const { response: response4, fire: fire4 } = useAxios()
  const { response: response6, fire: fire6 } = useAxios()
  const user = useAxios()
  const [loading, setLoading] = useState(true)

  React.useEffect(() => {
    const portalTarget = document.getElementById('header-portal')

    if (portalTarget) {
      setPortal(portalTarget)
    }

    setLoading(true)

    const prom1 = fire(getUserEducationList(id))
    const prom2 = fire2(getUserLanguagesList(id))
    const prom3 = fire3(getUserOtherInfo(id))
    const prom6 = fire6(getArticles('/communities/list', { userId: id }))
    const prom7 = user.fire(getUserById(id))

    void Promise.all([prom1, prom2, prom3, prom6, prom7])
      .catch((error: any) => {
        toast.error(error.message)
      })
      .finally(() => {
        setLoading(false)
      })
  }, [])

  const breadcrumbs = useMemo(() => {
    if (user.response?.data) {
      return [
        ...BREADCRUMBS,
        {
          label: user.response.data.name + ' ' + user.response.data.lastName,
          path: `/participant/${id}`
        }
      ]
    }

    return [...BREADCRUMBS, { label: '', path: `/participant/${id}` }]
  }, [user.response?.data, id])

  const features = useMemo(() => {
    const data = response3?.data?.features ?? {}

    return Object.keys(data).reduce<string[]>((acc, key: string) => {
      if (data[key]) {
        acc.push(key)
      }

      return acc
    }, [])
  }, [response3?.data])

  const vendors = useMemo(
    () => ConfigStore.privateConfig?.socialsVendors || [],
    [ConfigStore.privateConfig]
  )

  const items = useMemo(
    () => [
      {
        label: 'Дата рождения',
        value: user.response?.data?.birthday
      },
      {
        label: 'Телефон',
        value: user.response?.data?.phone
      },
      {
        label: 'Электронная почта',
        value: user.response?.data?.email
      }
    ],
    [user.response?.data]
  )

  const userSocials = useMemo<{ [key: string]: IUserSocial }>(() => {
    const userSocials = user.response?.data?.socials || []

    return userSocials.reduce(
      (acc: { [key: string]: IUserSocial }, userSocial: IUserSocial) => {
        const hasVendor = vendors.find(({ id }) => id === userSocial.id)

        if (hasVendor) {
          acc[userSocial.id] = userSocial
        }

        return acc
      },
      {}
    )
  }, [ConfigStore.privateConfig, user.response?.data])

  if (user.error?.statusCode === 401) {
    return (
      <div className={'page'}>
        <NotFound />
      </div>
    )
  }

  return (
    <div className={'page'}>
      <Breadcrumbs
        className={'page__breadcrumbs'}
        breadcrumbs={breadcrumbs}
      ></Breadcrumbs>
      {portal &&
        createPortal(<Breadcrumbs breadcrumbs={breadcrumbs} />, portal)}

      <div className="page__content profile-page">
        {user.error ? (
          <p>{user.error.message}</p>
        ) : (
          <>
            {!user.loading && user.response?.data && (
              <div className="profile-page__row">
                <Card className="profile-page-card">
                  <div className="profile-page-card__wrapper">
                    <Avatar
                      tag={'div'}
                      size={'xl'}
                      url={user.response.data.avatarUrl}
                    ></Avatar>
                    <div>
                      <header className={'card-head'}>
                        <div className={'card-head__title text-h3'}>
                          {user.response.data.name}{' '}
                          {user.response.data.lastName}
                        </div>

                        {user.response.data.balls !== undefined ? (
                          <div className={'profile-page-card__balls'}>
                            <Balls
                              balls={user.response.data.balls}
                              icon="/i/svg/rating.svg"
                            ></Balls>
                          </div>
                        ) : null}
                      </header>
                      <div className="card-subtitle text-body-small text-secondary-color">
                        {user.response.data.role}
                      </div>

                      <div className="card-content">
                        <div className="profile-page-card__items">
                          {items.map((item, index) => {
                            return item.value ? (
                              <div className="item" key={index}>
                                <div className="item__label text-caption">
                                  {item.label}
                                </div>
                                <div className="item__value text-body-bold">
                                  {item.value}
                                </div>
                              </div>
                            ) : undefined
                          })}
                        </div>
                        <div className="profile-page-card__socials">
                          {vendors.map((vendor) => {
                            const userSocial = userSocials[vendor.id as string]
                            if (!userSocial) return undefined

                            const Tag = userSocial.href ? 'a' : 'div'

                            return (
                              <Tag
                                key={vendor.id}
                                href={userSocial.href || undefined}
                                target={userSocial.href ? '_blank' : undefined}
                                rel={
                                  userSocial.href
                                    ? 'noopener noreferrer'
                                    : undefined
                                }
                              >
                                <img src={vendor.icon} alt={vendor.name} />
                                <span>{userSocial.text}</span>
                              </Tag>
                            )
                          })}
                        </div>
                      </div>
                    </div>
                  </div>
                </Card>
              </div>
            )}

            <div className="profile-page__row">
              <div className="profile-page__col">
                <Card className={'profile-page-card'}>
                  {loading ? (
                    <>
                      <div>
                        <header className={'card-head d-block'}>
                          <Skeleton width={'50%'}></Skeleton>
                        </header>

                        <div className="card-content">
                          <Skeleton count={2}></Skeleton>
                        </div>
                      </div>
                      <div className={'mt-md-12 mt-sm-10'}>
                        <header className={'card-head d-block'}>
                          <Skeleton width={'50%'}></Skeleton>
                        </header>

                        <div className="card-content">
                          <Skeleton count={2}></Skeleton>
                        </div>
                      </div>
                    </>
                  ) : (
                    <>
                      <header className={'card-head'}>
                        <div className={'card-head__title text-h3'}>
                          Образование
                        </div>
                      </header>
                      <div className="card-content profile-page-card__education">
                        {response?.data?.length ? (
                          response.data.map((item: IEducationItem) => {
                            return (
                              <EducationInfoItem
                                item={item}
                                key={item.id}
                              ></EducationInfoItem>
                            )
                          })
                        ) : (
                          <p className={'text-caption text-secondary-color'}>
                            Нет информации
                          </p>
                        )}
                      </div>
                    </>
                  )}
                </Card>

                <Card className={'profile-page-card'}>
                  {loading ? (
                    <>
                      <div>
                        <header className={'card-head d-block'}>
                          <Skeleton width={'50%'}></Skeleton>
                        </header>

                        <div className="card-content">
                          <Skeleton count={2}></Skeleton>
                        </div>
                      </div>
                    </>
                  ) : (
                    <>
                      <header className={'card-head'}>
                        <div className={'card-head__title text-h3'}>
                          Знание языков
                        </div>
                      </header>
                      <div className={'card-content'}>
                        {response2?.data?.length ? (
                          <div className={'row row-gap-8'}>
                            {response2.data.map((item: ILanguageItem) => {
                              return (
                                <Chip
                                  className={'text-body-small'}
                                  key={item.id}
                                >
                                  <>
                                    {item.name.label}{' '}
                                    <span className={'text-secondary-color'}>
                                      {item.level.label}
                                    </span>
                                  </>
                                </Chip>
                              )
                            })}
                          </div>
                        ) : (
                          <p className={'text-caption text-secondary-color'}>
                            Нет информации
                          </p>
                        )}
                      </div>
                    </>
                  )}
                </Card>
              </div>
              <div className="profile-page__col">
                {loading || response3?.data?.about ? (
                  <Card className={'profile-page-card'}>
                    {loading ? (
                      <>
                        <header className={'card-head d-block'}>
                          <Skeleton width={'35%'}></Skeleton>
                        </header>
                        <div className="card-content">
                          <Skeleton count={3}></Skeleton>
                        </div>
                      </>
                    ) : (
                      <>
                        <header className={'card-head'}>
                          <div className={'card-head__title text-h3'}>
                            О себе
                          </div>
                        </header>
                        <div className={'card-content'}>
                          <ExpandedText
                            text={response3?.data?.about}
                            limit={250}
                          ></ExpandedText>
                        </div>
                      </>
                    )}
                  </Card>
                ) : null}
                {loading || features.length ? (
                  <Card className={'profile-page-card'}>
                    {loading ? (
                      <>
                        <div>
                          <header className={'card-head d-block'}>
                            <Skeleton width={'50%'}></Skeleton>
                          </header>

                          <div className="card-content">
                            <Skeleton count={2}></Skeleton>
                          </div>
                        </div>
                      </>
                    ) : (
                      <>
                        <header className={'card-head'}>
                          <div className={'card-head__title text-h3'}>
                            Интересы
                          </div>
                        </header>

                        <div className={'card-content'}>
                          <ExpandedContent
                            previewCount={12}
                            render={(limit) => (
                              <>
                                {features.length ? (
                                  <div className={'row row-gap-8'}>
                                    {features.map(
                                      (f: string, index: number) =>
                                        index < limit && (
                                          <Chip key={f}>{f}</Chip>
                                        )
                                    )}
                                  </div>
                                ) : (
                                  <p
                                    className={
                                      'text-body-small text-secondary-color'
                                    }
                                  >
                                    Нет информации
                                  </p>
                                )}
                              </>
                            )}
                            showControls={features.length > 12}
                          ></ExpandedContent>
                        </div>
                      </>
                    )}
                  </Card>
                ) : null}
                {loading || response6?.data?.data.items.length ? (
                  <Card className={'profile-page-card'}>
                    {loading ? (
                      <>
                        <div>
                          <header className={'card-head d-block'}>
                            <Skeleton width={'50%'}></Skeleton>
                          </header>

                          <div className="card-content">
                            <Skeleton count={2}></Skeleton>
                          </div>
                        </div>
                      </>
                    ) : (
                      <>
                        <header className={'card-head'}>
                          <div className={'card-head__title text-h3'}>
                            Сообщества
                          </div>
                        </header>

                        <div className={'card-content'}>
                          <ExpandedContent
                            previewCount={2}
                            render={(limit) => (
                              <>
                                {response6?.data?.data.items.length ? (
                                  <div className={'row row-gap-8'}>
                                    {response6.data.data.items.map(
                                      (item: ArticleData, index: number) =>
                                        index < limit && (
                                          <CommunityItem
                                            item={item}
                                            key={item.id}
                                          ></CommunityItem>
                                        )
                                    )}
                                  </div>
                                ) : (
                                  <p
                                    className={
                                      'text-body-small text-secondary-color'
                                    }
                                  >
                                    Нет информации
                                  </p>
                                )}
                              </>
                            )}
                            showControls={features.length > 12}
                          ></ExpandedContent>
                        </div>
                      </>
                    )}
                  </Card>
                ) : null}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  )
})

export default ParticipantPage
